/**
 * ONE source of truth for "what may this operator see", shared by the sidebar,
 * the route gate, the landing redirect and the assign-pages modals.
 *
 * It holds two lists, and the difference between them is the whole design:
 *
 *   pages      THIS USER's menu tree  (`cmsPages` at login, refreshed from
 *              `GET /api/Auth/GetPageAccess`). What they were GRANTED.
 *   rolePages  EVERY assignable page  (`GET /api/RolePage/GetRolePages`).
 *              What CAN be granted - and therefore what may be enforced.
 *
 * Before this existed the sidebar fetched the first list, `MainRoutes` fetched
 * it a second time and threw the answer away, and the assign modal fetched the
 * second. Three requests, three opinions, no enforcement.
 *
 * ---------------------------------------------------------------------------
 * WHAT HAPPENS WHEN A REQUEST FAILS - deliberately, not by accident
 *
 * Both lists are cached in `localStorage`, so a blip falls back to the previous
 * answer. If there is no answer at all:
 *
 *   - no master list -> fall back to the DATED SNAPSHOT in
 *     `config/pageAccess.js`. `GetRolePages` may well be administrator-only -
 *     it was called from one screen, Users - and treating an empty master list
 *     as "enforce nothing" would silently disable the gate for exactly the
 *     restricted operators it exists for. See `assignableOwners`.
 *   - no user list, request FAILED -> gate OPEN. A denial we cannot prove is
 *     not a denial, and locking an operator out of the whole CMS because one
 *     GET failed is worse than the status quo this replaced. The API still
 *     authorises every call on its own.
 *   - no user list, request SUCCEEDED and was empty -> gate CLOSED. That is a
 *     real answer: this user holds no pages, and they get the no-permission
 *     screen.
 *
 * `enforced` reports which of those we are in, so the gate never has to guess.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import { isEmpty } from 'lodash';

import {
  API_URL,
  getStoredCmsPages,
  getStoredRolePages,
  securityHeaders,
  setStoredRolePages,
} from '../Constants';
import {
  assignableOwners,
  canAccessPath,
  landingPath,
  logUngatedScreens,
  ownersOf,
  NO_PERMISSION_PATH,
} from '../services/pageAccess';

const PageAccessContext = createContext(null);

/** Shape returned when there is no provider above - signed out, or a test. */
const EMPTY = {
  isLoading: false,
  enforced: false,
  pages: [],
  rolePages: [],
  granted: new Set(),
  assignable: new Set(),
  canAccess: () => true,
  landing: NO_PERMISSION_PATH,
  refresh: () => {},
};

export const usePageAccess = () => useContext(PageAccessContext) ?? EMPTY;

export const PageAccessProvider = ({ children }) => {
  const hasSession = !isEmpty(localStorage.getItem('token'));
  const userId = localStorage.getItem('userId');

  // Seeded from storage so the first paint is already correct for a returning
  // operator - no flash of an empty sidebar, and no window in which the gate
  // has to hold the screen back.
  const [pages, setPages] = useState(() => getStoredCmsPages() ?? []);
  const [rolePages, setRolePages] = useState(() => getStoredRolePages() ?? []);

  const [pagesResolved, setPagesResolved] = useState(() =>
    Boolean(getStoredCmsPages())
  );
  const [rolesResolved, setRolesResolved] = useState(() =>
    Boolean(getStoredRolePages())
  );

  /**
   * True once the request came back successfully, whatever it contained.
   *
   * This is what separates "this user has no pages" (enforce, show the
   * no-permission screen) from "we could not find out" (do not enforce).
   */
  const [pagesConfirmedEmpty, setPagesConfirmedEmpty] = useState(false);

  const [reloadToken, setReloadToken] = useState(0);
  const refresh = useCallback(() => setReloadToken((n) => n + 1), []);

  /* ---------------------------------------------- this user's granted pages */
  useEffect(() => {
    if (!hasSession || isEmpty(userId)) {
      setPagesResolved(true);
      return undefined;
    }

    let cancelled = false;

    axios
      .get(`${API_URL}/api/Auth/GetPageAccess?UserId=${userId}`, {
        headers: securityHeaders,
      })
      .then((response) => {
        if (cancelled) return;

        const fromApi = Array.isArray(response?.data) ? response.data : [];
        const stored = getStoredCmsPages();

        /**
         * `GetPageAccess` returns `nested: []` on every row, while
         * `POST /api/Auth/Login -> cmsPages` returns the full tree for the same
         * user at the same moment (verified 2026-09-22, still true). The two
         * carry the SAME grants, so for permission purposes either will do -
         * but the sidebar renders this list, and a flat one loses the grouping.
         *
         * Prefer whichever actually has children. This keeps working unchanged,
         * and starts preferring the API, the day the backend fixes it.
         */
        const hasNesting = (rows) =>
          Array.isArray(rows) && rows.some((row) => !isEmpty(row?.nested));

        const preferred =
          stored && hasNesting(stored) && !hasNesting(fromApi) ? stored : fromApi;

        setPages(preferred);
        setPagesConfirmedEmpty(preferred.length === 0);
        setPagesResolved(true);
      })
      .catch(() => {
        if (cancelled) return;
        // Keep whatever login stored. `pagesConfirmedEmpty` stays false, so a
        // user whose grants we could not read is not locked out.
        setPages(getStoredCmsPages() ?? []);
        setPagesResolved(true);
      });

    return () => {
      cancelled = true;
    };
  }, [hasSession, userId, reloadToken]);

  /* ------------------------------------------------ the master page list */
  useEffect(() => {
    if (!hasSession) {
      setRolesResolved(true);
      return undefined;
    }

    let cancelled = false;

    axios
      .get(`${API_URL}/api/RolePage/GetRolePages`, { headers: securityHeaders })
      .then((response) => {
        if (cancelled) return;

        // The envelope is not applied to this endpoint yet; accept both.
        const body = response?.data;
        const rows = Array.isArray(body) ? body : body?.data;

        if (Array.isArray(rows) && rows.length > 0) {
          setRolePages(rows);
          setStoredRolePages(rows);
        }
        setRolesResolved(true);
      })
      .catch(() => {
        if (cancelled) return;
        setRolePages(getStoredRolePages() ?? []);
        setRolesResolved(true);
      });

    return () => {
      cancelled = true;
    };
  }, [hasSession, reloadToken]);

  const granted = useMemo(() => ownersOf(pages), [pages]);

  /**
   * `fromApi` is false when we are running on the dated snapshot in
   * `config/pageAccess.js` because `GetRolePages` could not be read — most
   * likely because it is administrator-only. Worth knowing in the console; the
   * gate behaves the same either way.
   */
  const { owners: assignable, fromApi: assignableFromApi } = useMemo(
    () => assignableOwners(rolePages),
    [rolePages]
  );

  useEffect(() => {
    logUngatedScreens(assignable);
    if (!assignableFromApi && hasSession && rolesResolved) {
      // eslint-disable-next-line no-console
      console.info(
        '[pageAccess] GetRolePages unavailable — enforcing the snapshot in ' +
          'config/pageAccess.js (DEFAULT_ASSIGNABLE_SEGMENTS).'
      );
    }
  }, [assignable, assignableFromApi, hasSession, rolesResolved]);

  const isLoading = hasSession && !(pagesResolved && rolesResolved);

  /**
   * Do we know enough to refuse anybody?
   *
   * Only a real answer about THIS user counts: some granted pages, or a
   * confirmed empty response. A request that simply failed is not an answer,
   * and must not read as "holds nothing".
   */
  const enforced =
    hasSession && (granted.size > 0 || pagesConfirmedEmpty);

  const canAccess = useCallback(
    (pathname) =>
      enforced ? canAccessPath(pathname, granted, assignable) : true,
    [enforced, granted, assignable]
  );

  const landing = useMemo(() => landingPath(pages), [pages]);

  const value = useMemo(
    () => ({
      isLoading,
      enforced,
      pages,
      rolePages,
      granted,
      assignable,
      canAccess,
      landing,
      refresh,
    }),
    [
      isLoading,
      enforced,
      pages,
      rolePages,
      granted,
      assignable,
      canAccess,
      landing,
      refresh,
    ]
  );

  return (
    <PageAccessContext.Provider value={value}>
      {children}
    </PageAccessContext.Provider>
  );
};

export default PageAccessContext;
